<template lang="pug">
study-candidate.study-management-candidate(
  ref='template',
  fullNamePlaceholder='氏名',
  statusPlaceholder='ステータス',
  :userRole='userRole',
  :currentLink='currentLink',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :breadcrumbs='breadcrumbs',
  :breadcrumbMoveEvent='breadcrumbMoveEvent',
  :gradeSelectOptions='gradeSelectOptions',
  :searchEvent='searchEvent',
  :downloadEvent='downloadEvent',
  :sort='sort',
  :studentDatas='studentDatas',
  :pagenationStatus='pagenationStatus',
  :pagenationEvent='pagenationEvent',
  @sort-changed='sortChanged'
)
  template(#sideMenuSeparateText) 設定
  template(#logoutText) サインアウト
  template(#tabLabel) 生徒
  template(#searchButtonText) 絞り込む
  template(#downloadIconLabel) ダウンロード
  template(#userNameHeader) 氏名
  template(#bcbtCodeHeader) ID
  template(#gradeNameHeader) 学年
  template(#statusNameHeader) ステータス
  template(#possibilityHeader) 受験可否
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import GradesApi from '@/mixins/api/GradesApi'
import OriginalCurriculumsApi from '@/mixins/api/OriginalCurriculumsApi'
import StudyCandidate from '@/components/templates/manager/StudyCandidate.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import { StudentListType, StudentListSortType, StudentSearchParamsType } from '@/models/studentListModel'
import { InputItem } from '@/models/inputItem'
import { PaginationStatus } from '@/models/paginationModel'
import { LinkSetting, SideMenuSettings } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { InputItemPlaceholderSetting } from '@/const/DisplaySettings'
import { SortSettings } from '@/const/SortSettings'
import { StudySearchUtils } from '@/utils/StudySearchUtils'
import { StudentManagementUtils } from '@/utils/StudentManagementUtils'
import { StudyManagementUtils } from '@/utils/StudyManagementUtils'

/**
 * 塾管理者・テスト受験可能なユーザー画面
 */
@Component({
  components: {
    StudyCandidate,
  },
})
export default class StudyManagementCandidate extends Mixins(LoginMethods, GradesApi, OriginalCurriculumsApi) {
  // テンプレートへのアクセス
  @Ref() template!: StudyCandidate

  // テストID
  private get studyId(): number {
    return Number(this.$route.params.studyId)
  }

  // パンくず
  private breadcrumbs: breadcrumbType[] = [
    {
      label: 'テスト管理',
      location: { name: 'BranchStudyManagement' },
    },
    {
      label: '受験可能なユーザー_',
    },
  ]

  // 塾管理者権限
  private userRole: UserRoles = UserRoles.BRANCH

  // サイドメニューの現在リンク
  private currentLink: LinkSetting = SideMenuSettings.MANAGER_STUDY

  // ソート情報
  private sort: StudentListSortType | null = null

  // 検索用の学年選択オプション
  private gradeSelectOptions: InputItem[] = []

  // 生徒一覧情報
  private studentDatas: StudentListType[] = []

  // ページネーションの状態
  private pagenationStatus: PaginationStatus | null = null

  // 現在の検索パラメーター
  private currentSearchParams: StudentSearchParamsType | null = null

  // 現在のページ
  private get currentPage(): number {
    return this.pagenationStatus?.page || 1
  }

  // 初期表示処理
  private async mounted() {
    this.$loading.start()
    // デフォルトソート値をセットして1ページ目を検索する
    this.setDefaultSort()
    await this.search(1)
    // タイトル文字列を取得
    const apiResponse = await this.getOriginalCurriculumInfo(this.studyId)
    this.breadcrumbs[1].label += StudyManagementUtils.buildStudyContentTitle(apiResponse)
    // 学年選択オプションAPI呼び出し
    const gradeApiResponse = await this.getGrades()
    // 検索用の学年選択オプションを設定
    this.gradeSelectOptions = StudySearchUtils.buildGradeInputItems(
      StudySearchUtils.buildPlaceholder(InputItemPlaceholderSetting.MANAGER_GRADE_ALL),
      gradeApiResponse
    )
    this.$loading.complete()
  }

  // デフォルトソート値をセット
  private setDefaultSort() {
    this.sort = {
      userName: SortSettings.ASC,
      bcbtCode: SortSettings.ASC,
      gradeName: SortSettings.ASC,
      statusName: SortSettings.ASC,
      isPossible: SortSettings.ASC,
    }
  }

  // 検索ボタン押下イベント
  private async searchEvent() {
    this.$loading.start()
    // ソートを初期化
    this.setDefaultSort()
    // 1ページ目を検索
    await this.search(1)
    this.$loading.complete()
  }

  // ページ遷移イベント
  private async pagenationEvent(page: number) {
    this.$loading.start()
    // 指定されたページを検索
    await this.search(page)
    this.$loading.complete()
  }

  // ソート変更時イベント
  private async sortChanged(sort: StudentListSortType) {
    this.$loading.start()
    // ソートを更新
    this.sort = sort
    // 現在のページを検索
    await this.search(this.currentPage)
    this.$loading.complete()
  }

  // 検索処理
  private async search(page: number) {
    if (!this.sort) {
      return
    }
    // 受験可能な生徒一覧検索API呼び出し
    this.currentSearchParams = {
      page: page,
      gradeId: this.template.gradeValue ? Number(this.template.gradeValue) : null,
      userName: this.template.fullName,
      statusName: this.template.status,
      originalCurriculumId: this.studyId,
      userNameSort: this.sort.userName.code,
      bcbtCodeSort: this.sort.bcbtCode.code,
      gradeSort: this.sort.gradeName.code,
      statusNameSort: this.sort.statusName.code,
      isPossibleSort: this.sort.isPossible?.code || null,
    }
    const apiResponse = await this.getUserAvailableData(this.currentSearchParams, UserRoles.BRANCH)
    this.studentDatas = StudentManagementUtils.formatStudentDatas(apiResponse)
    this.pagenationStatus = StudentManagementUtils.getPagenationStatus(apiResponse)
  }

  // ダウンロードイベント
  private async downloadEvent() {
    if (this.currentSearchParams === null) {
      return
    }

    // ダウンロード処理
    this.$loading.start()
    try {
      // 直近の検索パラメーターを用いてエクセルデータを取得する
      const exportData = await this.exportStudentAvailableExcel(this.currentSearchParams, UserRoles.BRANCH)
      // aタグを生成し、hrefにデータを設定してclickを実行することでファイルをダウンロード
      const fileLink = document.createElement('a')
      fileLink.href = URL.createObjectURL(exportData)
      fileLink.download = '生徒受験可否一覧.xlsx'
      fileLink.click()
      this.$loading.complete()
    } catch {
      alert('エラーが発生しました')
    }
    this.$logger.log(`download with id: ${this.studyId}, params: ${JSON.stringify(this.currentSearchParams)}`)
  }

  // サイドメニュー遷移イベント
  private sideMenuLinkEvent(link: LinkSetting) {
    // 設定されたリンク先ページへ遷移
    this.$router.push({ name: `Branch${link.name}` })
  }

  // ログアウトイベント
  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'BranchLogin' })
  }

  // パンくず遷移イベント
  private breadcrumbMoveEvent(breadcrumb: breadcrumbType) {
    if (breadcrumb.location) {
      this.$router.push(breadcrumb.location)
    }
  }
}
</script>
